import {
  HTTPMethods,
  RequestExecutor,
} from "../../api-helpers/RequestExecutor";
import { getChatbotEndpoints } from "../../config";
import { captureErrorToSentry } from "../../utilities/sentryHelper";
import {
  SaveConflict,
  SaveJourneyRequest,
  SaveJourneyResult,
  VersionDiff,
  VersionPage,
} from "./journeyEditorModels";

// The builder stops waiting for a save after SAVE_WAIT_MS and saves the old way. The pod
// may start writing only within SAVE_WRITE_WITHIN_MS of receiving it; the gap covers the
// upload and the write itself.
const SAVE_WAIT_MS = 60000;
const SAVE_WRITE_WITHIN_MS = 45000;
const READ_BACK_WAIT_MS = 10000;
const GAVE_UP = Symbol("gave up waiting");

/** The chatbot pod's `ApiResponse` envelope. */
interface Envelope<T> {
  success?: boolean;
  data?: T;
  errors?: { message?: string }[];
}

/**
 * Reads and writes a journey through the journey editor's endpoints, the one write path
 * the builder and Claude share. The version endpoints answer only Bik staff.
 */
export class JourneyEditorService {
  /** The journey's version as stored now; a save has to be based on it. */
  async currentVersionId(
    storeId: string,
    flowId: string
  ): Promise<string | null> {
    const body = await this.call<{ version_id?: string | null }>(
      "/journeys/get",
      HTTPMethods.POST,
      // The canvas is the larger half of the answer and only the version is needed.
      { storeId, flowId, includeReactFlow: false }
    );
    this.throwUnlessSucceeded(body, "Could not read the journey's version");
    return body.data?.version_id ?? null;
  }

  /**
   * Saves through the editor. A save that is too slow, or gets no answer, is left to the
   * old save path, but only while the journey is still at the version this save was based
   * on: otherwise someone saved meanwhile, and the conflict is shown instead.
   */
  async saveJourney(request: SaveJourneyRequest): Promise<SaveJourneyResult> {
    let answer: Envelope<SaveData> | typeof GAVE_UP;
    try {
      answer = await this.waitAtMost(
        this.call<SaveData>("/journeys/save", HTTPMethods.POST, {
          storeId: request.storeId,
          flowId: request.flowId,
          nodes: request.nodes,
          reactFlow: request.reactFlow,
          stickyNotes: request.stickyNotes,
          baseVersionId: request.baseVersionId,
          authorEmail: request.authorEmail,
          writeWithinMs: SAVE_WRITE_WITHIN_MS,
          // The builder's Save keeps a journey that does not compile yet, as a draft.
          allowErrors: true,
        }),
        SAVE_WAIT_MS
      );
    } catch (err: any) {
      captureErrorToSentry(err, "Error in saving a journey through the editor");
      return this.afterSlowSave(request);
    }
    if (answer === GAVE_UP) {
      return this.afterSlowSave(request);
    }
    const body = answer;
    if (body.success === false && body.data?.timed_out) {
      return this.afterSlowSave(request);
    }
    const conflict = this.conflictIn(body);
    if (conflict) {
      return { versionId: null, conflict };
    }
    // Not the pod's own answer, such as FastAPI refusing the body before the save runs: whether
    // anything was written is read back, as for a save that got no answer.
    if (body.success !== true && body.success !== false) {
      return this.afterSlowSave(request);
    }
    this.throwUnlessSucceeded(body, "Could not save the journey");
    return { versionId: body.data?.version_id ?? null };
  }

  async listVersions(
    storeId: string,
    flowId: string,
    afterVersionId?: string
  ): Promise<VersionPage> {
    const query: { [key: string]: string | number } = {
      storeId,
      flowId,
      limit: 50,
    };
    if (afterVersionId) {
      query.afterVersionId = afterVersionId;
    }
    const body = await this.call<VersionPage>(
      "/journeys/versions/list",
      HTTPMethods.GET,
      query
    );
    this.throwUnlessSucceeded(body, "Could not load the version history");
    return body.data as VersionPage;
  }

  /** What going back to `versionId` would change, compared with the journey now. */
  async diffFromNow(
    storeId: string,
    flowId: string,
    versionId: string
  ): Promise<VersionDiff> {
    const body = await this.call<VersionDiff>(
      "/journeys/versions/diff",
      HTTPMethods.GET,
      { storeId, flowId, fromVersionId: versionId }
    );
    this.throwUnlessSucceeded(body, "Could not compare the versions");
    return body.data as VersionDiff;
  }

  async restoreVersion(
    storeId: string,
    flowId: string,
    versionId: string,
    baseVersionId: string | null,
    authorEmail: string
  ): Promise<SaveJourneyResult> {
    const body = await this.call<{ version_id?: string | null } & ConflictData>(
      "/journeys/versions/restore",
      HTTPMethods.POST,
      { storeId, flowId, versionId, baseVersionId, authorEmail }
    );
    const conflict = this.conflictIn(body);
    if (conflict) {
      return { versionId: null, conflict };
    }
    this.throwUnlessSucceeded(body, "Could not restore the version");
    return { versionId: body.data?.version_id ?? null };
  }

  /**
   * Records the journey as stored as a version, after it was saved the old way, so a draft
   * or another tab opened on the version before gets a conflict instead of overwriting it.
   * The new version, or null when it could not be recorded.
   */
  async recordVersion(
    storeId: string,
    flowId: string,
    baseVersionId: string | null,
    authorEmail: string
  ): Promise<string | null> {
    try {
      const body = await this.call<
        { version_id?: string | null } & ConflictData
      >("/journeys/versions/record", HTTPMethods.POST, {
        storeId,
        flowId,
        baseVersionId,
        authorEmail,
      });
      this.throwUnlessSucceeded(body, "Could not record the journey's version");
      return body.data?.version_id ?? null;
    } catch (err: any) {
      // The save itself is done; the next save through the editor records it instead.
      captureErrorToSentry(
        err,
        "Error in recording a journey saved the old way"
      );
      return null;
    }
  }

  // Whether the slow save may have landed is read back from the journey's version.
  private async afterSlowSave(
    request: SaveJourneyRequest
  ): Promise<SaveJourneyResult> {
    let current: string | null | typeof GAVE_UP;
    try {
      current = await this.waitAtMost(
        this.currentVersionId(request.storeId, request.flowId),
        READ_BACK_WAIT_MS
      );
    } catch (err: any) {
      captureErrorToSentry(err, "Error in reading back a slow journey save");
      return { versionId: null, saveOldWay: true };
    }
    if (current === GAVE_UP || current === request.baseVersionId) {
      return { versionId: null, saveOldWay: true };
    }
    return {
      versionId: null,
      conflict: { currentVersionId: current, changedBy: null, changedAt: null },
    };
  }

  private async waitAtMost<T>(
    work: Promise<T>,
    ms: number
  ): Promise<T | typeof GAVE_UP> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const gaveUp = new Promise<typeof GAVE_UP>((resolve) => {
      timer = setTimeout(() => resolve(GAVE_UP), ms);
    });
    try {
      return await Promise.race([work, gaveUp]);
    } finally {
      clearTimeout(timer);
    }
  }

  private async call<T>(
    path: string,
    apiMethod: HTTPMethods,
    requestBody: object
  ): Promise<Envelope<T>> {
    const response: any = await RequestExecutor.fetch({
      apiPath: `${getChatbotEndpoints()}${path}`,
      apiMethod,
      requestBody,
    });
    if (!response?.data) {
      throw new Error(response?.error?.message || `No answer from ${path}`);
    }
    return response.data as Envelope<T>;
  }

  // A refused save on a stale version carries the version that won, and who saved it.
  // The pod reports it under a generic error code, so the data is what tells it apart.
  private conflictIn(body: Envelope<ConflictData>): SaveConflict | undefined {
    if (body.success !== false || !body.data?.current_version_id) {
      return undefined;
    }
    return {
      currentVersionId: body.data.current_version_id ?? null,
      changedBy: body.data.changed_by ?? null,
      changedAt: body.data.changed_at ?? null,
    };
  }

  private throwUnlessSucceeded(body: Envelope<unknown>, fallback: string) {
    if (body.success === false) {
      throw new Error(body.errors?.[0]?.message || fallback);
    }
  }
}

type SaveData = {
  version_id?: string | null;
  timed_out?: boolean;
} & ConflictData;

interface ConflictData {
  current_version_id?: string | null;
  changed_by?: string | null;
  changed_at?: number | null;
}
